import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FEATURE_TO_COINS } from 'shared/feature-to-coins'
import { Button } from 'ui/button'
import { Card } from 'ui/card'
import { highlightWord } from 'ui/flip-card'
import { toast } from 'ui/sonner'

import { LS_JWT_KEY } from '~/const/api'
import { useAccountStore } from '~/stores/account'
import { useCardsGameStore } from '~/stores/cards-game'
import { useSettingsStore } from '~/stores/settings'

type NoGuessedTextGeneratorProps = {
  className?: string
}

export const NoGuessedTextGenerator = ({ className }: NoGuessedTextGeneratorProps) => {
  const { t } = useTranslation()
  const notGuessedCards = useCardsGameStore(state => state.notGuessedCards)
  const unknownWords = notGuessedCards.map(card => card.learnText)
  const learnLanguageLabel = useSettingsStore(state => state.learnLanguage.label)
  const [summarizedText, setSummarizedText] = useState<string | null>(null)
  const decreaseCoins = useAccountStore(state => state.decreaseCoins)

  if (typeof summarizedText === 'string')
    return (
      <div className="flex flex-1 items-center">
        <Card className="flex h-3/4 w-full overflow-auto p-4">
          <p dangerouslySetInnerHTML={{ __html: highlightWord(summarizedText, unknownWords) }} />
        </Card>
      </div>
    )

  if (!notGuessedCards.length) return null

  return (
    <Button
      className={className}
      onClick={() => {
        const languageQuery = learnLanguageLabel ? `&language=${learnLanguageLabel}` : ''
        let isAnyDataReceived = false
        const eventSource = new EventSource(
          new URL(
            `example/summarize?words=${unknownWords.join(',')}${languageQuery}&token=${localStorage.getItem(
              LS_JWT_KEY,
            )}`,
            import.meta.env.VITE_API_BASE,
          ),
        )
        setSummarizedText('')
        eventSource.onmessage = ({ data }) => {
          isAnyDataReceived = true
          setSummarizedText(prevText => `${prevText}${data}`)
        }
        eventSource.onerror = () => {
          // TODO: server need to be refactored to handle errors properly
          eventSource.close()

          if (isAnyDataReceived) return decreaseCoins(FEATURE_TO_COINS.summarize)

          toast.error(t('You have reached the subscription limit'), {
            action: <Button onClick={() => toast.dismiss()}>{t('Change subscription')}</Button>,
          })
        }
      }}
      variant="secondary"
    >
      {t('Text from unknown words')}
    </Button>
  )
}
